import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';

import { getAlerts } from '../../api/services/Alerts';
import { useToast } from '../../contexts/ToastContext';
import { useEmployees } from '../../hooks/useEmployees';

interface IUseAlerts {
  alerts: Alerts[];
  setAlerts: Dispatch<SetStateAction<Alerts[]>>;
  loading: boolean;
  employees: Employee[];
  fetchAlerts: (query: { [x: string]: unknown }) => void;
  markResolved: (ids: number[]) => void;
}

// Safety-net ceiling on how long a tombstone can survive without server confirmation.
// The incidents-resolution cron runs every ~2 minutes (OPE-472), so 3 minutes covers
// the worst-case backend lag with margin.
const RESOLVED_BUFFER_TTL_MS = 180000;

// Params that are always present regardless of user-applied filters. Excluded from the
// isFiltered check so a bare date-range fetch is treated as unfiltered.
const FIXED_PARAMS = ['created_after', 'created_before'] as const;

export const useAlerts = (): IUseAlerts => {
  const [alerts, setAlerts] = useState<Alerts[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();
  // Tombstone set of ids resolved via WebSocket. Entries are dropped either by the
  // self-correct path in fetchAlerts (when an unfiltered fetch confirms the server has
  // reflected the resolution) or after RESOLVED_BUFFER_TTL_MS, whichever comes first.
  const recentlyResolvedRef = useRef<Set<number>>(new Set());

  const { employees } = useEmployees();

  const markResolved = useCallback((ids: number[]) => {
    ids.forEach((id) => {
      recentlyResolvedRef.current.add(id);
      setTimeout(() => recentlyResolvedRef.current.delete(id), RESOLVED_BUFFER_TTL_MS);
    });
  }, []);

  const fetchAlerts = useCallback(async (params: { [x: string]: unknown }) => {
    setAlerts([]);
    setLoading(true);
    const response = await getAlerts(params);

    if (response.type === 'error') {
      response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
    } else {
      const incidents = response.data.slimIncidents;
      // Only self-correct when no narrowing filter is applied — otherwise an id's
      // absence from the response could just mean it didn't match the filter, not
      // that the server has reflected the resolution.
      const isFiltered = (Object.keys(params) as (keyof typeof params)[])
        .filter((key) => !FIXED_PARAMS.includes(key as (typeof FIXED_PARAMS)[number]))
        .some((key) => {
          const v = params[key];

          return v !== undefined && v !== false && (!Array.isArray(v) || v.length > 0);
        });

      if (!isFiltered) {
        const serverIds = new Set(incidents.map((alert) => alert.id));

        recentlyResolvedRef.current.forEach((id) => {
          if (!serverIds.has(id)) recentlyResolvedRef.current.delete(id);
        });
      }

      setAlerts(incidents.filter((alert) => !recentlyResolvedRef.current.has(alert.id)));
    }

    setLoading(false);
  }, []);

  return { alerts, setAlerts, loading, fetchAlerts, employees, markResolved };
};
